import { assertConfigured, config } from './config';
import { searchAmenityPois, searchNamedPois, type AmenityPoi } from './poiSearch';
import { parseSearchIntent, type SearchIntent } from './searchIntent';
import type { LatLng, OrsGeocodeFeature, OrsGeocodeResponse } from './types';
import { haversineMeters } from './routing';
import { scheduleIdle } from './mainThread';

export interface GeocodeHit {
  name: string;
  location: LatLng;
  layer: string;
  raw: OrsGeocodeResponse['features'][number];
}

const ENDPOINT = '/v2/geocode/search';
const REVERSE_ENDPOINT = '/v2/geocode/reverse';

/** Live GPS / map focus for ranking. Falls back to config.defaultCenter. */
let searchFocus: LatLng | null = null;

/** Soft regional filter — blocks distant name collisions (e.g. NL streets). */
const CE_COUNTRIES = 'HU,AT,SK,CZ,SI,HR,RO,RS';

/** Street-only search: ~30 km around GPS / map focus. Not used when a city is named. */
const STREET_FOCUS_RADIUS_KM = 30;
const QUERY_CACHE_TTL_MS = 5 * 60_000;
const REVERSE_CACHE_TTL_MS = 30 * 60_000;
const PERSISTENT_QUERY_TTL_MS = 7 * 24 * 60 * 60_000;
const PERSISTENT_QUERY_STALE_MS = 30 * 24 * 60 * 60_000;
const PERSISTENT_REVERSE_TTL_MS = 7 * 24 * 60 * 60_000;
const PERSISTENT_REVERSE_STALE_MS = 30 * 24 * 60 * 60_000;
const PERSISTENT_QUERY_MAX = 400;
const PERSISTENT_REVERSE_MAX = 150;
const PERSISTENT_QUERY_KEY = 'navi.geocodeQuery.v1';
const PERSISTENT_REVERSE_KEY = 'navi.geocodeReverse.v1';

/** Lower = more important in the hits list (places before streets). */
const LAYER_RANK: Record<string, number> = {
  locality: 0,
  localadmin: 1,
  borough: 2,
  neighbourhood: 3,
  county: 4,
  region: 5,
  amenity: 5,
  venue: 6,
  address: 7,
  street: 8,
};

const COUNTRY_NAME_RE =
  /^(hungary|österreich|austria|slovakia|szlovákia|czechia|czech republic|csehország|slovenia|croatia|romania|serbia|deutschland|germany|hu|at|sk|cz)$/i;

type PlaceStreetSplit = { place: string; street: string };

type RankContext = {
  query: string;
  split: PlaceStreetSplit | null;
  /** Resolved place name from Pelias (e.g. "Veszprém"), when available. */
  anchorPlaceName: string | null;
  intent: SearchIntent | null;
  /** Brand / POI name when query is "Lidl Korneuburg" or bare "Lidl". */
  poiNameHint: string | null;
};

type BrandPlaceResolve = {
  nameHint: string;
  placeName: string | null;
  placeLocation: LatLng | null;
  placeHit: GeocodeHit | null;
};

type TimedEntry<T> = {
  ts: number;
  value: T;
};

export type GeocodeQuotaDebug = {
  remaining: number | null;
  resetAtUtcMs: number | null;
  limit: number | null;
  lastStatus: number | null;
  lastError: string | null;
  updatedAtMs: number | null;
};

export type GeocodeCacheDebug = {
  memoryQueries: number;
  memoryReverse: number;
  persistentQueries: number;
  persistentReverse: number;
};

type SlimGeocodeProps = {
  name?: string;
  locality?: string;
  localadmin?: string;
  borough?: string;
  street?: string;
  housenumber?: string;
};

type SlimGeocodeHit = {
  name: string;
  lat: number;
  lng: number;
  layer: string;
  props?: SlimGeocodeProps;
};

type PersistentQueryEntry = {
  ts: number;
  hits: SlimGeocodeHit[];
};

type PersistentReverseEntry = {
  ts: number;
  label: string;
};

type PersistentQueryStore = {
  order: string[];
  entries: Record<string, PersistentQueryEntry>;
};

type PersistentReverseStore = {
  order: string[];
  entries: Record<string, PersistentReverseEntry>;
};

const queryCache = new Map<string, TimedEntry<GeocodeHit[]>>();
const reverseCache = new Map<string, TimedEntry<string>>();
let queryStoreLoaded = false;
let queryStore: PersistentQueryStore | null = null;
let reverseStoreLoaded = false;
let reverseStore: PersistentReverseStore | null = null;
const quotaDebug: GeocodeQuotaDebug = {
  remaining: null,
  resetAtUtcMs: null,
  limit: null,
  lastStatus: null,
  lastError: null,
  updatedAtMs: null,
};

export function setGeocodeFocus(point: LatLng | null): void {
  searchFocus = point;
}

export function getGeocodeQuotaDebug(): GeocodeQuotaDebug {
  return { ...quotaDebug };
}

export function getGeocodeCacheDebug(): GeocodeCacheDebug {
  const qStore = ensureQueryStore();
  const rStore = ensureReverseStore();
  return {
    memoryQueries: queryCache.size,
    memoryReverse: reverseCache.size,
    persistentQueries: qStore ? Object.keys(qStore.entries).length : 0,
    persistentReverse: rStore ? Object.keys(rStore.entries).length : 0,
  };
}

/** Reverse-geocode a map pin into a human label (falls back to coordinates). */
export async function reverseGeocode(p: LatLng, signal?: AbortSignal): Promise<string> {
  assertConfigured();
  const cacheKey = `${p.lat.toFixed(5)},${p.lng.toFixed(5)}`;
  const cached = reverseCache.get(cacheKey);
  if (cached && Date.now() - cached.ts < REVERSE_CACHE_TTL_MS) return cached.value;

  const fromDisk = readPersistentReverse(cacheKey, PERSISTENT_REVERSE_TTL_MS);
  if (fromDisk) {
    reverseCache.set(cacheKey, { ts: fromDisk.ts, value: fromDisk.label });
    return fromDisk.label;
  }

  try {
    const params = new URLSearchParams({
      'point.lon': String(p.lng),
      'point.lat': String(p.lat),
      size: '1',
      layers: 'address,street,venue,locality',
    });
    const data = await request<OrsGeocodeResponse>(
      `${REVERSE_ENDPOINT}?${params.toString()}`,
      signal,
    );
    const feature = data.features[0];
    if (!feature) return formatCoordLabel(p);
    const label = formatGeocodeLabel(feature, formatCoordLabel(p)).trim();
    const value = label || formatCoordLabel(p);
    const ts = Date.now();
    reverseCache.set(cacheKey, { ts, value });
    pruneTimedMap(reverseCache, REVERSE_CACHE_TTL_MS, 80);
    savePersistentReverse(cacheKey, value, ts);
    return value;
  } catch (err) {
    const stale = reverseCache.get(cacheKey);
    if (stale) return stale.value;
    const diskStale = readPersistentReverse(cacheKey, PERSISTENT_REVERSE_STALE_MS);
    if (diskStale) return diskStale.label;
    throw err instanceof Error ? err : new Error(String(err));
  }
}

function formatCoordLabel(p: LatLng): string {
  return `${p.lat.toFixed(5)}, ${p.lng.toFixed(5)}`;
}

/**
 * Forward-geocodes a free-text query using ORS (Pelias) + Komoot Photon,
 * and Overpass amenity intent (fuel / hospital / pharmacy / …) or named
 * brand/POI lookup when there is no amenity keyword.
 *
 * Place-like queries rank localities first. “Place, street…” resolves the
 * place then streets. “Brand City” (e.g. Lidl Korneuburg) resolves the city
 * then searches venues / OSM name+brand nearby. All hits are lat/lng ready
 * for routing.
 */
export async function geocode(query: string, signal?: AbortSignal): Promise<GeocodeHit[]> {
  assertConfigured();

  const trimmed = query.trim();
  if (trimmed.length < 2) return [];
  const cacheKey = geocodeCacheKey(trimmed);
  const cached = queryCache.get(cacheKey);
  if (cached && Date.now() - cached.ts < QUERY_CACHE_TTL_MS) return cached.value;

  const fromDisk = readPersistentQuery(cacheKey, PERSISTENT_QUERY_TTL_MS);
  if (fromDisk) {
    queryCache.set(cacheKey, { ts: fromDisk.ts, value: fromDisk.hits });
    return fromDisk.hits;
  }

  const focus = searchFocus ?? config.defaultCenter;
  const split = splitPlaceAndStreet(trimmed);
  let intent = parseSearchIntent(trimmed);
  if (
    intent &&
    split &&
    intent.nameHint &&
    foldText(intent.nameHint) === foldText(split.place.split(',')[0] ?? split.place)
  ) {
    intent = { ...intent, nameHint: '' };
  }
  const placeLike = isPlaceLikeQuery(trimmed, split);
  let streets: GeocodeHit[] = [];
  let placesFocused: GeocodeHit[] = [];
  let placesGlobal: GeocodeHit[] = [];
  let venues: GeocodeHit[] = [];
  let anchored: GeocodeHit[] = [];
  let anchorPlaceName: string | null = null;
  let poiNameHint: string | null = null;
  let poiFocus = focus;
  let firstErr: unknown = null;

  const photonPromise = fetchPhotonHits(trimmed, focus, signal).catch(() => [] as GeocodeHit[]);
  let amenityPromise: Promise<GeocodeHit[]> = intent
    ? mapAmenityPois(
        searchAmenityPois(intent.filters, focus, {
          nameHint: intent.nameHint,
          signal,
        }),
      )
    : Promise.resolve([] as GeocodeHit[]);
  let namedPhotonPromise: Promise<GeocodeHit[]> = Promise.resolve([]);

  try {
    if (split) {
      const anchoredPack = await placeAnchoredStreets(split.place, split.street, signal);
      anchored = anchoredPack?.hits ?? [];
      anchorPlaceName = anchoredPack?.placeName ?? null;
      if (anchored.length === 0) {
        placesFocused = await fetchHits(
          split.place,
          null,
          {
            layers: 'locality,localadmin,borough,neighbourhood,county',
            circleKm: null,
            country: CE_COUNTRIES,
            size: 6,
          },
          signal,
        );
      }
    } else if (placeLike && !intent) {
      placesFocused = await fetchHits(
        trimmed,
        focus,
        {
          layers: 'locality,localadmin,borough,neighbourhood,county',
          circleKm: null,
          country: CE_COUNTRIES,
          size: 10,
        },
        signal,
      );
      const strongPlace = hasStrongPlaceHit(placesFocused, trimmed);
      if (!strongPlace) {
        placesGlobal = await fetchHits(
          trimmed,
          null,
          {
            layers: 'locality,localadmin,borough,neighbourhood',
            circleKm: null,
            country: CE_COUNTRIES,
            size: 8,
          },
          signal,
        );
      }

      if (!strongPlace && !hasStrongPlaceHit(placesGlobal, trimmed)) {
        const brandPlace = await resolveBrandPlaceQuery(trimmed, signal);
        poiNameHint = brandPlace.nameHint;
        if (brandPlace.placeHit) {
          anchorPlaceName = brandPlace.placeName;
          poiFocus = brandPlace.placeLocation ?? focus;
          placesFocused = [brandPlace.placeHit, ...placesFocused];
        }
        const venueCircleKm = 30;
        const namedRadiusM = 30_000;
        const venuePromise = fetchHits(
          brandPlace.nameHint,
          poiFocus,
          {
            layers: 'venue',
            circleKm: venueCircleKm,
            country: CE_COUNTRIES,
            size: 8,
          },
          signal,
        );
        amenityPromise = mapAmenityPois(
          searchNamedPois(brandPlace.nameHint, poiFocus, {
            radiusM: namedRadiusM,
            signal,
          }),
        );
        if (
          brandPlace.placeHit &&
          foldText(brandPlace.nameHint) !== foldText(trimmed)
        ) {
          namedPhotonPromise = fetchPhotonHits(
            brandPlace.nameHint,
            poiFocus,
            signal,
          ).catch(() => [] as GeocodeHit[]);
        }
        venues = await venuePromise;
      }
    } else {
      const orsVenueQuery = intent?.nameHint
        ? `${intent.nameHint} ${intent.id}`
        : trimmed;
      [streets, placesFocused, venues] = await Promise.all([
        intent
          ? Promise.resolve([] as GeocodeHit[])
          : fetchHits(
              trimmed,
              focus,
              {
                layers: 'street,address',
                circleKm: STREET_FOCUS_RADIUS_KM,
                country: CE_COUNTRIES,
                size: 10,
              },
              signal,
            ),
        fetchHits(
          intent?.nameHint || trimmed,
          focus,
          {
            layers: 'locality,localadmin,borough,neighbourhood,county',
            circleKm: null,
            country: CE_COUNTRIES,
            size: 8,
          },
          signal,
        ),
        fetchHits(
          orsVenueQuery,
          focus,
          { layers: 'venue', circleKm: 80, country: CE_COUNTRIES, size: 6 },
          signal,
        ),
      ]);
    }
  } catch (err) {
    firstErr = err;
  }

  const [photonHits, amenityHits, namedPhotonHits] = await Promise.all([
    photonPromise,
    amenityPromise,
    namedPhotonPromise,
  ]);

  const places = [...placesGlobal, ...placesFocused];
  if (
    streets.length === 0 &&
    places.length === 0 &&
    venues.length === 0 &&
    anchored.length === 0 &&
    photonHits.length === 0 &&
    namedPhotonHits.length === 0 &&
    amenityHits.length === 0
  ) {
    const stale = queryCache.get(cacheKey);
    if (stale) return stale.value;
    const diskStale = readPersistentQuery(cacheKey, PERSISTENT_QUERY_STALE_MS);
    if (diskStale) return diskStale.hits;
    if (firstErr) {
      throw firstErr instanceof Error ? firstErr : new Error(String(firstErr));
    }
    return [];
  }

  const pool = buildHitPool({
    split,
    streets,
    places,
    venues,
    anchored,
    anchorPlaceName,
  });

  const merged = [...amenityHits, ...namedPhotonHits, ...photonHits, ...pool];
  const ctx: RankContext = {
    query: trimmed,
    split,
    anchorPlaceName,
    intent,
    poiNameHint,
  };
  const cityHint =
    anchorPlaceName ?? split?.place ?? null;
  const cityLocked = cityHint !== null && (split !== null || poiNameHint !== null);
  const scoped = cityLocked
    ? [
        ...merged.filter((h) => h.layer === 'amenity' || h.layer === 'venue'),
        ...filterToAnchoredCity(
          merged.filter((h) => h.layer !== 'amenity' && h.layer !== 'venue'),
          cityHint,
        ),
      ]
    : merged;
  const enriched =
    cityHint === null
      ? scoped
      : scoped.map((h) =>
          h.layer === 'street' || h.layer === 'address'
            ? { ...h, name: ensureCityInLabel(h.name, cityHint) }
            : h.layer === 'amenity' || h.layer === 'venue'
              ? { ...h, name: ensureCityInLabel(h.name, cityHint) }
              : h,
        );

  const ranked = rankAndDedupe(enriched, ctx);
  const poiAware = intent !== null || poiNameHint !== null;
  const result = poiAware
    ? sortAmenityAware(ranked, poiFocus, {
        id: intent?.id ?? 'poi',
        filters: intent?.filters ?? [],
        nameHint: intent?.nameHint || poiNameHint || '',
      }).slice(0, 14)
    : ranked.slice(0, 12);
  const ts = Date.now();
  queryCache.set(cacheKey, { ts, value: result });
  pruneTimedMap(queryCache, QUERY_CACHE_TTL_MS, 120);
  if (result.length > 0) savePersistentQuery(cacheKey, result, ts);
  return result;
}

function mapAmenityPois(pois: Promise<AmenityPoi[]>): Promise<GeocodeHit[]> {
  return pois.then((list) =>
    list.map((p) =>
      makeSyntheticHit({
        name: formatAmenityLabel(p.name, p.kind),
        lat: p.lat,
        lng: p.lng,
        layer: 'amenity',
        locality: undefined,
      }),
    ),
  );
}

/**
 * “Lidl Korneuburg” → brand Lidl + locality Korneuburg.
 * Skipped when the full query is already a strong place name.
 */
async function resolveBrandPlaceQuery(
  query: string,
  signal?: AbortSignal,
): Promise<BrandPlaceResolve> {
  const tokens = query.trim().split(/\s+/).filter(Boolean);
  if (tokens.length < 2) {
    return {
      nameHint: query.trim(),
      placeName: null,
      placeLocation: null,
      placeHit: null,
    };
  }

  const tryCounts = tokens.length >= 3 ? [2, 1] : [1];
  for (const n of tryCounts) {
    if (tokens.length <= n) continue;
    const placeHint = tokens.slice(-n).join(' ');
    const nameHint = tokens.slice(0, -n).join(' ');
    if (nameHint.length < 2 || placeHint.length < 2) continue;
    if (STREET_HINT_RE.test(placeHint) || STREET_HINT_RE.test(nameHint)) continue;

    const placeHits = await fetchHits(
      placeHint,
      null,
      {
        layers: 'locality,localadmin,borough,neighbourhood',
        circleKm: null,
        country: CE_COUNTRIES,
        size: 6,
      },
      signal,
    );
    const place = pickPlaceHit(placeHint, placeHits);
    if (!place) continue;
    if (textMatchRank(primaryName(place), placeHint) > 1) continue;

    return {
      nameHint,
      placeName: primaryName(place),
      placeLocation: place.location,
      placeHit: place,
    };
  }

  return {
    nameHint: query.trim(),
    placeName: null,
    placeLocation: null,
    placeHit: null,
  };
}

function hasStrongPlaceHit(hits: GeocodeHit[], query: string): boolean {
  return hits.some((hit) => isPlaceLayer(hit.layer) && textMatchRank(primaryName(hit), query) <= 1);
}

function buildHitPool(args: {
  split: PlaceStreetSplit | null;
  streets: GeocodeHit[];
  places: GeocodeHit[];
  venues: GeocodeHit[];
  anchored: GeocodeHit[];
  anchorPlaceName: string | null;
}): GeocodeHit[] {
  const { split, streets, places, venues, anchored, anchorPlaceName } = args;

  if (!split) {
    return [...places, ...venues, ...streets, ...anchored];
  }

  const placeKey = anchorPlaceName ?? split.place;
  const placeOnly = dedupePlacesForSplit(places, placeKey);

  // “City, street” — the city is fixed. Never surface the same street name
  // from another town, even if GPS focus or ORS ranking prefers it.
  if (anchorPlaceName || anchored.length > 0) {
    const city = anchorPlaceName ?? split.place;
    const inCity = anchored.filter((h) => isInAnchoredCity(h, city));
    return inCity.length > 0 ? [...placeOnly, ...inCity] : placeOnly;
  }

  return dedupePlacesForSplit(
    [...placeOnly, ...places.filter((h) => isPlaceLayer(h.layer))],
    split.place,
  );
}

function dedupePlacesForSplit(places: GeocodeHit[], placeQuery: string): GeocodeHit[] {
  const needle = placeNeedle(placeQuery);
  const seen = new Set<string>();
  const out: GeocodeHit[] = [];
  for (const hit of places) {
    if (!isPlaceLayer(hit.layer)) continue;
    const key = foldText(primaryName(hit));
    if (seen.has(key)) continue;
    if (needle.length >= 2 && textMatchRank(primaryName(hit), needle) > 3) continue;
    seen.add(key);
    out.push(hit);
  }
  return out;
}

/**
 * “Várgesztes, Hungary, vadá” → place + street fragment.
 * Ignores trailing country-only tokens; requires a non-country last segment.
 */
function splitPlaceAndStreet(query: string): PlaceStreetSplit | null {
  const parts = query
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length < 2) return null;

  const street = parts[parts.length - 1]!;
  if (street.length < 2 || COUNTRY_NAME_RE.test(street)) return null;

  const placeParts = parts.slice(0, -1).filter((p) => !COUNTRY_NAME_RE.test(p));
  if (placeParts.length === 0) return null;

  return { place: placeParts.join(', '), street };
}

async function placeAnchoredStreets(
  placeQuery: string,
  streetQuery: string,
  signal?: AbortSignal,
): Promise<{ hits: GeocodeHit[]; placeName: string } | null> {
  const placeHits = await fetchHits(
    placeQuery,
    null,
    {
      layers: 'locality,localadmin,borough,neighbourhood',
      circleKm: null,
      country: CE_COUNTRIES,
      size: 8,
    },
    signal,
  );
  const place = pickPlaceHit(placeQuery, placeHits);
  if (!place) return null;

  const hits = await streetsNearPlace(place, streetQuery, signal);
  const city = primaryName(place);
  const inCity = hits.filter((h) => isInAnchoredCity(h, city));
  return {
    hits: inCity.map((h) => ({ ...h, name: ensureCityInLabel(h.name, city) })),
    placeName: city,
  };
}

function pickPlaceHit(placeQuery: string, candidates: GeocodeHit[]): GeocodeHit | null {
  if (candidates.length === 0) return null;
  const needle = placeNeedle(placeQuery);
  const placeLayers = candidates.filter((h) => isPlaceLayer(h.layer));
  const pool = placeLayers.length > 0 ? placeLayers : candidates;

  const exact = pool.filter((h) => foldText(primaryName(h)) === needle);
  if (exact.length > 0) {
    return exact.sort(
      (a, b) => textMatchRank(primaryName(a), needle) - textMatchRank(primaryName(b), needle),
    )[0]!;
  }

  return pool.sort(
    (a, b) => textMatchRank(primaryName(a), needle) - textMatchRank(primaryName(b), needle),
  )[0]!;
}

async function streetsNearPlace(
  place: GeocodeHit,
  streetQuery: string,
  signal?: AbortSignal,
): Promise<GeocodeHit[]> {
  const city = primaryName(place);
  const opts = {
    layers: 'street,address',
    circleKm: null,
    country: CE_COUNTRIES,
    size: 12,
  } as const;
  const withCity = (street: string) => `${street}, ${city}`;

  let hits = streetsInCity(
    await fetchHits(withCity(streetQuery), null, opts, signal),
    city,
  );
  if (hits.length > 0) return pickStreetHits(hits, streetQuery);

  for (const variant of streetTypoVariants(streetQuery)) {
    hits = streetsInCity(await fetchHits(withCity(variant), null, opts, signal), city);
    if (hits.length > 0) {
      const picked = pickStreetHits(hits, streetQuery);
      if (picked.length > 0) return picked;
    }
  }

  const folded = foldText(streetQuery);
  if (folded.length >= 4) {
    hits = streetsInCity(
      await fetchHits(withCity(folded.slice(0, 4)), null, { ...opts, size: 15 }, signal),
      city,
    );
    const picked = pickStreetHits(hits, streetQuery);
    if (picked.length > 0) return picked;
  }

  return [];
}

function streetsInCity(hits: GeocodeHit[], anchorCity: string): GeocodeHit[] {
  return hits.filter((h) => isInAnchoredCity(h, anchorCity));
}

function filterToAnchoredCity(hits: GeocodeHit[], anchorCity: string): GeocodeHit[] {
  const needle = placeNeedle(anchorCity);
  return hits.filter((h) => {
    if (isPlaceLayer(h.layer)) {
      const name = foldText(primaryName(h));
      return name === needle || textMatchRank(primaryName(h), needle) <= 1;
    }
    return isInAnchoredCity(h, anchorCity);
  });
}

function streetTypoVariants(streetQuery: string): string[] {
  const folded = foldText(streetQuery);
  const out = new Set<string>();
  if (folded.startsWith('haput')) out.add(`haupt${folded.slice(4)}`);
  if (folded.startsWith('haupt')) out.add(`haput${folded.slice(4)}`);
  for (let i = 0; i < folded.length; i++) {
    for (const [from, to] of [
      ['a', 'u'],
      ['u', 'a'],
      ['e', 'i'],
      ['i', 'e'],
    ] as const) {
      if (folded[i] === from) out.add(folded.slice(0, i) + to + folded.slice(i + 1));
    }
  }
  out.delete(folded);
  return [...out];
}

function pickStreetHits(hits: GeocodeHit[], streetQuery: string): GeocodeHit[] {
  const ranked = hits
    .map((h) => ({ h, rank: textMatchRank(streetHaystack(h), streetQuery) }))
    .filter((x) => x.rank <= 5)
    .sort((a, b) => a.rank - b.rank || a.h.name.length - b.h.name.length);
  return ranked.map((x) => x.h);
}

type FetchOpts = {
  layers: string;
  circleKm: number | null;
  country: string | undefined;
  size: number;
};

async function fetchHits(
  text: string,
  focus: LatLng | null,
  opts: FetchOpts,
  signal?: AbortSignal,
): Promise<GeocodeHit[]> {
  const params = new URLSearchParams({
    text,
    size: String(opts.size),
    layers: opts.layers,
  });

  if (focus) {
    params.set('focus.point.lon', String(focus.lng));
    params.set('focus.point.lat', String(focus.lat));
  }

  if (opts.circleKm !== null && focus) {
    params.set('boundary.circle.lon', String(focus.lng));
    params.set('boundary.circle.lat', String(focus.lat));
    params.set('boundary.circle.radius', String(opts.circleKm));
  }

  if (opts.country) {
    params.set('boundary.country', opts.country);
  }

  const data = await request<OrsGeocodeResponse>(`${ENDPOINT}?${params.toString()}`, signal);
  return data.features
    .filter((f) => {
      const layer = f.properties.layer ?? '';
      return opts.layers.split(',').includes(layer);
    })
    .map((f) => ({
      name: formatGeocodeLabel(f, text),
      location: { lat: f.geometry.coordinates[1], lng: f.geometry.coordinates[0] },
      layer: f.properties.layer ?? '',
      raw: f,
    }));
}

type PhotonFeature = {
  geometry?: { coordinates?: [number, number] };
  properties?: {
    name?: string;
    street?: string;
    housenumber?: string;
    city?: string;
    locality?: string;
    district?: string;
    county?: string;
    state?: string;
    country?: string;
    type?: string;
    osm_value?: string;
    osm_key?: string;
  };
};

type PhotonResponse = { features?: PhotonFeature[] };

function photonLayer(props: PhotonFeature['properties']): string {
  const t = (props?.type ?? '').toLowerCase();
  if (t === 'house' || t === 'address') return 'address';
  if (t === 'street' || t === 'road') return 'street';
  if (t === 'city' || t === 'town' || t === 'village' || t === 'locality') return 'locality';
  if (t === 'district' || t === 'suburb' || t === 'neighbourhood') return 'neighbourhood';
  if (t === 'county') return 'county';
  if (t === 'state') return 'region';
  const key = (props?.osm_key ?? '').toLowerCase();
  if (key === 'amenity' || key === 'shop' || key === 'tourism' || key === 'healthcare') {
    return 'amenity';
  }
  return 'venue';
}

function formatPhotonLabel(props: PhotonFeature['properties'], fallback: string): string {
  if (!props) return fallback;
  const name = props.name?.trim();
  const street = props.street?.trim();
  const hn = props.housenumber?.trim();
  const city =
    props.city?.trim() ||
    props.locality?.trim() ||
    props.district?.trim() ||
    props.county?.trim() ||
    '';
  const road = street || name;
  if (road && (hn || street)) {
    const head = hn ? `${street || name} ${hn}` : road;
    return joinLabelParts([head, city || null, city ? null : props.country?.trim() || null]);
  }
  if (name) {
    return joinLabelParts([name, city && foldText(city) !== foldText(name) ? city : null]);
  }
  return fallback;
}

async function fetchPhotonHits(
  text: string,
  focus: LatLng,
  signal?: AbortSignal,
): Promise<GeocodeHit[]> {
  const params = new URLSearchParams({
    q: text,
    lat: String(focus.lat),
    lon: String(focus.lng),
    limit: '8',
    lang: 'en',
  });

  const url =
    config.mode === 'proxy'
      ? `${config.proxyUrl}/v2/photon?${params.toString()}`
      : `https://photon.komoot.io/api/?${params.toString()}`;

  try {
    const res = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal,
    });
    if (!res.ok) return [];
    const data = (await res.json()) as PhotonResponse;
    const features = data.features ?? [];
    const out: GeocodeHit[] = [];
    for (const f of features) {
      const coords = f.geometry?.coordinates;
      if (!coords || coords.length < 2) continue;
      const lng = coords[0]!;
      const lat = coords[1]!;
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
      const layer = photonLayer(f.properties);
      const name = formatPhotonLabel(f.properties, text);
      out.push(
        makeSyntheticHit({
          name,
          lat,
          lng,
          layer,
          locality: f.properties?.city?.trim() || f.properties?.locality?.trim(),
          street: f.properties?.street?.trim(),
          housenumber: f.properties?.housenumber?.trim(),
          propName: f.properties?.name?.trim(),
        }),
      );
    }
    return out;
  } catch (err) {
    if ((err as Error).name === 'AbortError') return [];
    return [];
  }
}

function makeSyntheticHit(args: {
  name: string;
  lat: number;
  lng: number;
  layer: string;
  locality?: string;
  street?: string;
  housenumber?: string;
  propName?: string;
}): GeocodeHit {
  const raw: OrsGeocodeFeature = {
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [args.lng, args.lat] },
    properties: {
      layer: args.layer,
      name: args.propName ?? args.name.split(',')[0]?.trim() ?? args.name,
      locality: args.locality,
      street: args.street,
      housenumber: args.housenumber,
      label: args.name,
    },
  };
  return {
    name: args.name,
    location: { lat: args.lat, lng: args.lng },
    layer: args.layer,
    raw,
  };
}

function formatAmenityLabel(name: string, kind: string): string {
  const pretty = kind.replace(/_/g, ' ');
  if (foldText(name) === foldText(pretty)) return name;
  if (foldText(name).includes(foldText(pretty))) return name;
  return `${name} (${pretty})`;
}

/** Amenity intent: keep amenities first, nearest first within that band. */
function sortAmenityAware(
  hits: GeocodeHit[],
  focus: LatLng,
  intent: SearchIntent,
): GeocodeHit[] {
  const hint = foldText(intent.nameHint);
  return [...hits].sort((a, b) => {
    const amA = a.layer === 'amenity' || a.layer === 'venue' ? 0 : 1;
    const amB = b.layer === 'amenity' || b.layer === 'venue' ? 0 : 1;
    if (amA !== amB) return amA - amB;
    if (hint) {
      const ha = foldText(primaryName(a)).includes(hint) ? 0 : 1;
      const hb = foldText(primaryName(b)).includes(hint) ? 0 : 1;
      if (ha !== hb) return ha - hb;
    }
    const da = haversineMeters(focus, a.location);
    const db = haversineMeters(focus, b.location);
    return da - db;
  });
}

function foldText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .trim();
}

function primaryName(hit: GeocodeHit): string {
  const fromProps = hit.raw.properties.name?.trim();
  if (fromProps) return fromProps;
  const head = hit.name.split(',')[0]?.trim();
  return head || hit.name;
}

function placeNeedle(placeQuery: string): string {
  return foldText(placeQuery.split(',')[0] ?? placeQuery);
}

function hitLocalityText(hit: GeocodeHit): string {
  const fromProps = hit.raw.properties.locality?.trim();
  if (fromProps) return fromProps;
  const parts = hit.name.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 2) return parts[1]!;
  return '';
}

/** True only when the hit belongs to the user-named city — not a neighbour
 *  with the same street name, not a fuzzy county/localadmin partial. */
function isInAnchoredCity(hit: GeocodeHit, anchorCity: string): boolean {
  const needle = placeNeedle(anchorCity);
  if (needle.length < 2) return false;

  const p = hit.raw.properties;
  const adminTokens = [p.locality, p.localadmin, p.borough]
    .filter((x): x is string => Boolean(x?.trim()))
    .map((x) => foldText(x));
  if (adminTokens.some((tok) => tok === needle)) return true;

  const labelParts = hit.name
    .split(',')
    .map((part) => foldText(part.trim()))
    .filter(Boolean);
  return labelParts.some((part) => part === needle);
}

/** Lower is better. 0 = locality matches the anchored place. */
function localityRank(hit: GeocodeHit, placeQuery: string): number {
  const needle = placeNeedle(placeQuery);
  if (needle.length < 2) return 5;
  if (isInAnchoredCity(hit, placeQuery)) return 0;

  const locality = foldText(hitLocalityText(hit));
  if (locality.length >= 4 && locality.startsWith(needle)) return 1;
  if (locality.length >= 4 && needle.length >= 4 && needle.startsWith(locality)) return 1;

  const label = foldText(hit.name);
  if (label.includes(needle)) return 2;
  return 8;
}

/**
 * Fuzzy-ish prefix / token score against a free-text needle.
 * Lower is better. Used for place-only queries and street fragments.
 */
function textMatchRank(haystack: string, needleRaw: string): number {
  const needle = foldText(needleRaw);
  if (needle.length < 2) return 10;

  const name = foldText(haystack);
  if (!name) return 10;
  if (name === needle) return 0;

  const tokens = name.split(/[\s/.-]+/).filter(Boolean);
  if (tokens.some((tok) => tok === needle)) return 1;

  if (name.startsWith(`${needle} `) || name.startsWith(`${needle}-`)) return 2;

  const prefixTok = tokens.find((tok) => tok.startsWith(needle));
  if (prefixTok) {
    if (prefixTok.length === needle.length) return 1;
    return 3 + Math.min(5, prefixTok.length - needle.length);
  }

  const nearTok = tokens.find((tok) => streetTypoDistance(tok, needle) <= 2);
  if (nearTok) return 4;

  if (name.includes(needle)) return 7;
  return 10;
}

function streetTypoDistance(a: string, b: string): number {
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > 2) return 99;
  const m = a.length;
  const n = b.length;
  const dp = Array.from({ length: m + 1 }, () => Array<number>(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i]![0] = i;
  for (let j = 0; j <= n; j++) dp[0]![j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i]![j] = Math.min(
        dp[i - 1]![j]! + 1,
        dp[i]![j - 1]! + 1,
        dp[i - 1]![j - 1]! + cost,
      );
    }
  }
  return dp[m]![n]!;
}

function streetHaystack(hit: GeocodeHit): string {
  const street = hit.raw.properties.street?.trim();
  if (street) return street;
  return primaryName(hit);
}

const STREET_HINT_RE =
  /\b(utca|út|u\.|tér|körút|sétány|street|strasse|str\.|road|avenue|lane)\b/i;

function isPlaceLikeQuery(query: string, split: PlaceStreetSplit | null): boolean {
  if (split) return false;
  return !STREET_HINT_RE.test(query);
}

function queryNeedle(ctx: RankContext): string {
  return foldText(ctx.query.split(',')[0] ?? ctx.query);
}

function isPlaceLayer(layer: string): boolean {
  return (
    layer === 'locality' ||
    layer === 'localadmin' ||
    layer === 'borough' ||
    layer === 'neighbourhood' ||
    layer === 'county' ||
    layer === 'region'
  );
}

/** Demote “Veszprémi utca” / “Veszprém utca” when the user typed a city name. */
function placeOnlyStreetPenalty(hit: GeocodeHit, ctx: RankContext): number {
  if (hit.layer !== 'street' && hit.layer !== 'address') return 0;
  const needle = queryNeedle(ctx);
  if (needle.length < 2) return 0;
  const name = foldText(streetHaystack(hit));
  if (name === needle) return 6;
  if (name === `${needle}i` || name.startsWith(`${needle}i `)) return 8;
  if (name.startsWith(`${needle} `) || name.startsWith(`${needle}-`)) return 5;
  if (name.startsWith(needle)) return 4;
  return 2;
}

function nameMatchRank(hit: GeocodeHit, ctx: RankContext): number {
  if (ctx.split) {
    return textMatchRank(streetHaystack(hit), ctx.split.street);
  }
  const needle =
    ctx.poiNameHint ||
    ctx.intent?.nameHint ||
    ctx.query.split(',')[0] ||
    ctx.query;
  return textMatchRank(primaryName(hit), needle);
}

function clarityBand(rank: number): number {
  if (rank <= 0) return 0;
  if (rank <= 1) return 1;
  if (rank <= 3) return 2;
  return 3;
}

function isExactPlaceHit(hit: GeocodeHit, ctx: RankContext): boolean {
  if (!isPlaceLayer(hit.layer)) return false;
  return nameMatchRank(hit, ctx) === 0;
}

function rankAndDedupe(hits: GeocodeHit[], ctx: RankContext): GeocodeHit[] {
  const placeForLocality = ctx.anchorPlaceName ?? ctx.split?.place ?? null;
  const placeLike = isPlaceLikeQuery(ctx.query, ctx.split);
  const cityLocked = ctx.split !== null && placeForLocality !== null;
  const pool = cityLocked ? filterToAnchoredCity(hits, placeForLocality) : hits;
  const amenityIntent = ctx.intent !== null || ctx.poiNameHint !== null;

  const sorted = [...pool].sort((a, b) => {
    if (amenityIntent) {
      const amA = a.layer === 'amenity' || a.layer === 'venue' ? 0 : 1;
      const amB = b.layer === 'amenity' || b.layer === 'venue' ? 0 : 1;
      if (amA !== amB) return amA - amB;
    }

    if (placeForLocality) {
      const locDiff = localityRank(a, placeForLocality) - localityRank(b, placeForLocality);
      if (locDiff !== 0) return locDiff;
    }

    let nameA = nameMatchRank(a, ctx);
    let nameB = nameMatchRank(b, ctx);
    if (ctx.split) {
      if ((a.layer === 'street' || a.layer === 'address') && nameA <= 4) nameA -= 2;
      if ((b.layer === 'street' || b.layer === 'address') && nameB <= 4) nameB -= 2;
      if (isPlaceLayer(a.layer)) nameA += 2;
      if (isPlaceLayer(b.layer)) nameB += 2;
    }
    if (placeLike) {
      nameA += placeOnlyStreetPenalty(a, ctx);
      nameB += placeOnlyStreetPenalty(b, ctx);
    }

    const exactA = isExactPlaceHit(a, ctx);
    const exactB = isExactPlaceHit(b, ctx);
    if (exactA !== exactB) return exactA ? -1 : 1;

    if (placeLike) {
      const bandDiff = clarityBand(nameA) - clarityBand(nameB);
      if (bandDiff !== 0) return bandDiff;
      const layerDiff = (LAYER_RANK[a.layer] ?? 9) - (LAYER_RANK[b.layer] ?? 9);
      if (layerDiff !== 0) return layerDiff;
      if (nameA !== nameB) return nameA - nameB;
      return a.name.length - b.name.length;
    }

    if (nameA !== nameB) return nameA - nameB;
    const layerDiff = (LAYER_RANK[a.layer] ?? 9) - (LAYER_RANK[b.layer] ?? 9);
    if (layerDiff !== 0) return layerDiff;
    return a.name.length - b.name.length;
  });

  const seen = new Set<string>();
  const out: GeocodeHit[] = [];
  for (const hit of sorted) {
    const key = dedupeKey(hit);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(hit);
  }
  return out;
}

/**
 * Streets often arrive as many Pelias segments with the same label but
 * different centroids — collapse those. House numbers stay distinct.
 */
function dedupeKey(hit: GeocodeHit): string {
  const layer = hit.layer;
  if (layer === 'street' || layer === 'address') {
    const street = foldText(streetHaystack(hit));
    const city = cityForDedupe(hit);
    const hn = hit.raw.properties.housenumber?.trim() || '';
    if (layer === 'address' && hn) {
      return `addr|${street}|${foldText(hn)}|${city}`;
    }
    return `street|${street}|${city}`;
  }
  return `place|${foldText(primaryName(hit))}|${hit.location.lat.toFixed(3)}|${hit.location.lng.toFixed(3)}`;
}

function cityForDedupe(hit: GeocodeHit): string {
  const fromProps = hitLocalityText(hit) || placeContext(hit.raw.properties);
  if (fromProps.trim()) return foldText(fromProps);
  const parts = hit.name
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length >= 2) {
    const maybeCity = parts[1]!;
    if (!COUNTRY_NAME_RE.test(maybeCity)) return foldText(maybeCity);
  }
  return '';
}

function formatGeocodeLabel(
  f: OrsGeocodeResponse['features'][number],
  fallback: string,
): string {
  const p = f.properties;
  const layer = p.layer ?? '';
  const city = placeContext(p);
  const country = p.country?.trim() || '';
  const street = p.street?.trim();
  const housenumber = p.housenumber?.trim();
  const name = p.name?.trim();
  const isRoad = layer === 'street' || layer === 'address' || Boolean(street);

  if (isRoad) {
    const road = street || name || fallback;
    const head = housenumber ? `${road} ${housenumber}` : road;
    return joinLabelParts([head, city || null, city ? null : country || null]);
  }

  if (name) {
    return joinLabelParts([
      name,
      city && foldText(city) !== foldText(name) ? city : null,
      country || null,
    ]);
  }

  if (p.label?.trim()) {
    const raw = p.label.trim();
    if (city && !labelHasToken(raw, city)) {
      return joinLabelParts([raw.replace(/,\s*[^,]+$/u, '').trim() || raw, city]);
    }
    return raw;
  }

  return fallback;
}

function placeContext(p: OrsGeocodeResponse['features'][number]['properties']): string {
  return (
    p.locality?.trim() ||
    p.localadmin?.trim() ||
    p.borough?.trim() ||
    p.neighbourhood?.trim() ||
    p.county?.trim() ||
    p.region?.trim() ||
    ''
  );
}

function labelHasToken(label: string, token: string): boolean {
  const hay = foldText(label);
  const needle = foldText(token);
  if (!needle) return true;
  return hay === needle || hay.includes(needle);
}

function joinLabelParts(parts: Array<string | null | undefined>): string {
  const out: string[] = [];
  for (const part of parts) {
    if (!part) continue;
    if (out.some((x) => labelHasToken(x, part) || labelHasToken(part, x))) continue;
    out.push(part);
  }
  return out.join(', ');
}

function ensureCityInLabel(label: string, city: string): string {
  const cityName = city.split(',')[0]?.trim() || city;
  if (!cityName || labelHasToken(label, cityName)) return label;
  const withoutCountry = label.replace(/,\s*[^,]+$/u, '').trim() || label;
  if (labelHasToken(withoutCountry, cityName)) return label;
  return `${withoutCountry}, ${cityName}`;
}

async function request<T>(pathWithQuery: string, signal?: AbortSignal): Promise<T> {
  if (config.mode === 'proxy') {
    const res = await fetch(`${config.proxyUrl}${pathWithQuery}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal,
    });
    updateQuotaDebug(res);
    if (!res.ok) {
      throw await toHttpError(res, 'Geocoding');
    }
    return (await res.json()) as T;
  }

  const res = await fetch(`https://api.openrouteservice.org${pathWithQuery}`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: config.orsKey,
    },
    signal,
  });
  updateQuotaDebug(res);
  if (!res.ok) {
    throw await toHttpError(res, 'Geocoding');
  }
  return (await res.json()) as T;
}

async function toHttpError(res: Response, label: string): Promise<Error> {
  let detail = `${res.status} ${res.statusText}`;
  try {
    const body = (await res.json()) as { error?: string };
    if (body?.error) detail += ` — ${body.error}`;
  } catch {
    /* ignore body parse errors */
  }
  if (res.status === 403 && /quota exceeded/i.test(detail)) {
    quotaDebug.lastError = detail;
    return new Error(
      'A keresesi limit atmenetileg elfogyott. Varj kicsit, vagy pontositsd a lekerdest.',
    );
  }
  quotaDebug.lastError = detail;
  return new Error(`${label} hívás sikertelen: ${detail}`);
}

function updateQuotaDebug(res: Response): void {
  quotaDebug.lastStatus = res.status;
  quotaDebug.updatedAtMs = Date.now();
  const remaining = parseHeaderInt(res.headers.get('x-ratelimit-remaining'));
  const reset = parseHeaderInt(res.headers.get('x-ratelimit-reset'));
  const limit = parseHeaderInt(res.headers.get('x-ratelimit-limit'));
  if (remaining !== null) quotaDebug.remaining = remaining;
  if (reset !== null) quotaDebug.resetAtUtcMs = reset * 1000;
  if (limit !== null) quotaDebug.limit = limit;
  if (res.ok) quotaDebug.lastError = null;
}

function parseHeaderInt(value: string | null): number | null {
  if (!value) return null;
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) ? n : null;
}

function geocodeCacheKey(query: string): string {
  const focus = searchFocus ?? config.defaultCenter;
  return `${foldText(query)}|${focus.lat.toFixed(2)}|${focus.lng.toFixed(2)}`;
}

function slimifyHit(hit: GeocodeHit): SlimGeocodeHit {
  const p = hit.raw.properties;
  const props: SlimGeocodeProps = {};
  if (p.name?.trim()) props.name = p.name.trim();
  if (p.locality?.trim()) props.locality = p.locality.trim();
  if (p.localadmin?.trim()) props.localadmin = p.localadmin.trim();
  if (p.borough?.trim()) props.borough = p.borough.trim();
  if (p.street?.trim()) props.street = p.street.trim();
  if (p.housenumber?.trim()) props.housenumber = p.housenumber.trim();
  const slim: SlimGeocodeHit = {
    name: hit.name,
    lat: hit.location.lat,
    lng: hit.location.lng,
    layer: hit.layer,
  };
  if (Object.keys(props).length > 0) slim.props = props;
  return slim;
}

function hydrateHit(slim: SlimGeocodeHit): GeocodeHit {
  const props = slim.props ?? {};
  return {
    name: slim.name,
    location: { lat: slim.lat, lng: slim.lng },
    layer: slim.layer,
    raw: {
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [slim.lng, slim.lat] },
      properties: {
        layer: slim.layer,
        name: props.name,
        locality: props.locality,
        localadmin: props.localadmin,
        borough: props.borough,
        street: props.street,
        housenumber: props.housenumber,
      },
    },
  };
}

function ensureQueryStore(): PersistentQueryStore | null {
  if (typeof window === 'undefined') return null;
  if (queryStoreLoaded) return queryStore;
  queryStoreLoaded = true;
  try {
    const raw = window.localStorage.getItem(PERSISTENT_QUERY_KEY);
    if (!raw) {
      queryStore = { order: [], entries: {} };
      return queryStore;
    }
    const parsed = JSON.parse(raw) as Partial<PersistentQueryStore>;
    if (!parsed || typeof parsed.entries !== 'object' || !Array.isArray(parsed.order)) {
      queryStore = { order: [], entries: {} };
      return queryStore;
    }
    queryStore = {
      order: parsed.order.filter((k) => typeof k === 'string'),
      entries: parsed.entries as Record<string, PersistentQueryEntry>,
    };
  } catch {
    queryStore = { order: [], entries: {} };
  }
  return queryStore;
}

function ensureReverseStore(): PersistentReverseStore | null {
  if (typeof window === 'undefined') return null;
  if (reverseStoreLoaded) return reverseStore;
  reverseStoreLoaded = true;
  try {
    const raw = window.localStorage.getItem(PERSISTENT_REVERSE_KEY);
    if (!raw) {
      reverseStore = { order: [], entries: {} };
      return reverseStore;
    }
    const parsed = JSON.parse(raw) as Partial<PersistentReverseStore>;
    if (!parsed || typeof parsed.entries !== 'object' || !Array.isArray(parsed.order)) {
      reverseStore = { order: [], entries: {} };
      return reverseStore;
    }
    reverseStore = {
      order: parsed.order.filter((k) => typeof k === 'string'),
      entries: parsed.entries as Record<string, PersistentReverseEntry>,
    };
  } catch {
    reverseStore = { order: [], entries: {} };
  }
  return reverseStore;
}

function writeQueryStore(store: PersistentQueryStore): void {
  if (typeof window === 'undefined') return;
  scheduleIdle(() => {
    try {
      window.localStorage.setItem(PERSISTENT_QUERY_KEY, JSON.stringify(store));
    } catch {
      /* quota / private mode */
    }
  });
}

function writeReverseStore(store: PersistentReverseStore): void {
  if (typeof window === 'undefined') return;
  scheduleIdle(() => {
    try {
      window.localStorage.setItem(PERSISTENT_REVERSE_KEY, JSON.stringify(store));
    } catch {
      /* quota / private mode */
    }
  });
}

function touchLruOrder(order: string[], key: string): void {
  const idx = order.indexOf(key);
  if (idx >= 0) order.splice(idx, 1);
  order.push(key);
}

function prunePersistentStore<T extends { ts: number }>(
  store: { order: string[]; entries: Record<string, T> },
  maxAgeMs: number,
  maxSize: number,
): void {
  const now = Date.now();
  for (const key of [...store.order]) {
    const entry = store.entries[key];
    if (!entry || now - entry.ts > maxAgeMs) {
      delete store.entries[key];
      const idx = store.order.indexOf(key);
      if (idx >= 0) store.order.splice(idx, 1);
    }
  }
  while (store.order.length > maxSize) {
    const oldest = store.order.shift();
    if (!oldest) break;
    delete store.entries[oldest];
  }
}

function readPersistentQuery(
  cacheKey: string,
  maxAgeMs: number,
): { ts: number; hits: GeocodeHit[] } | null {
  const store = ensureQueryStore();
  if (!store) return null;
  prunePersistentStore(store, PERSISTENT_QUERY_STALE_MS, PERSISTENT_QUERY_MAX);
  const entry = store.entries[cacheKey];
  if (!entry || Date.now() - entry.ts > maxAgeMs) return null;
  if (!Array.isArray(entry.hits) || entry.hits.length === 0) return null;
  touchLruOrder(store.order, cacheKey);
  writeQueryStore(store);
  return { ts: entry.ts, hits: entry.hits.map(hydrateHit) };
}

function savePersistentQuery(cacheKey: string, hits: GeocodeHit[], ts: number): void {
  const store = ensureQueryStore();
  if (!store) return;
  store.entries[cacheKey] = { ts, hits: hits.map(slimifyHit) };
  touchLruOrder(store.order, cacheKey);
  prunePersistentStore(store, PERSISTENT_QUERY_STALE_MS, PERSISTENT_QUERY_MAX);
  writeQueryStore(store);
}

function readPersistentReverse(
  cacheKey: string,
  maxAgeMs: number,
): { ts: number; label: string } | null {
  const store = ensureReverseStore();
  if (!store) return null;
  prunePersistentStore(store, PERSISTENT_REVERSE_STALE_MS, PERSISTENT_REVERSE_MAX);
  const entry = store.entries[cacheKey];
  if (!entry || Date.now() - entry.ts > maxAgeMs) return null;
  if (typeof entry.label !== 'string' || !entry.label.trim()) return null;
  touchLruOrder(store.order, cacheKey);
  writeReverseStore(store);
  return { ts: entry.ts, label: entry.label };
}

function savePersistentReverse(cacheKey: string, label: string, ts: number): void {
  const store = ensureReverseStore();
  if (!store) return;
  store.entries[cacheKey] = { ts, label };
  touchLruOrder(store.order, cacheKey);
  prunePersistentStore(store, PERSISTENT_REVERSE_STALE_MS, PERSISTENT_REVERSE_MAX);
  writeReverseStore(store);
}

function pruneTimedMap<T>(
  map: Map<string, TimedEntry<T>>,
  ttlMs: number,
  maxSize: number,
): void {
  const now = Date.now();
  for (const [key, entry] of map) {
    if (now - entry.ts > ttlMs) map.delete(key);
  }
  while (map.size > maxSize) {
    const first = map.keys().next().value;
    if (!first) break;
    map.delete(first);
  }
}
